import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BoardStages } from 'app/trello/models/board-stages';
import { Lists } from 'app/trello/models/lists';
import { PrimeNg } from 'app/trello/primeng.imports';
import { BoardStagesFetcher } from 'app/trello/services/board-stages-fetcher';
import { BoardStagesSender } from 'app/trello/services/board-stages-sender';
import { ListsFetcher } from 'app/trello/services/lists-fetcher';
import { TrelloService } from 'app/trello/services/trello.service';
import { Templating } from 'app/trello/templating.imports';
import isEqual from 'lodash-es/isEqual';
import { BehaviorSubject, combineLatest, EMPTY, Subscription, switchMap } from 'rxjs';

@Component({
  imports: [
    PrimeNg,
    Templating,
    FormsModule,
  ],
  selector: 'app-configuration-component',
  templateUrl: './configuration.component.html',
})
export class ConfigurationComponent implements OnInit, OnDestroy {

  protected dataLoaded = false;
  protected lists: Lists | undefined;
  protected selectedListStages: { [key: string]: string } = {};

  protected options = [
    {name: 'Unassigned', value: 'unassigned'},
    {name: 'Pending', value: 'pending'},
    {name: 'In Progress', value: 'inProgress'},
    {name: 'Completed', value: 'completed'},
  ];

  private boardStagesRequestQueue = new BehaviorSubject<BoardStages | null>(null);
  private boardStagesRequestQueueSubscription: Subscription | undefined;

  private originalBoardStages: BoardStages | undefined;

  constructor(
    private readonly boardStagesFetcher: BoardStagesFetcher,
    private readonly boardStagesSender: BoardStagesSender,
    private readonly listsFetcher: ListsFetcher,
    private readonly trelloService: TrelloService, // required
  ) {
  }

  ngOnInit(): void {

    const now = new Date().toISOString();

    combineLatest([
      this.boardStagesFetcher.fetch(),
      this.listsFetcher.fetch(now),
    ])
      .subscribe(([boardStages, lists]) => {

        this.lists = lists;

        this.lists?.lists.forEach((list) => {

          if (boardStages.pending.includes(list.listId)) {
            this.selectedListStages[list.listId] = 'pending';

          } else if (boardStages.inProgress.includes(list.listId)) {
            this.selectedListStages[list.listId] = 'inProgress';

          } else if (boardStages.completed.includes(list.listId)) {
            this.selectedListStages[list.listId] = 'completed';

          } else {
            this.selectedListStages[list.listId] = 'unassigned';
          }
        });

        this.originalBoardStages = boardStages;

        this.dataLoaded = true;
      });

    this.boardStagesRequestQueueSubscription =
      this.boardStagesRequestQueue
        .pipe(
          switchMap((updatedBoardStages) => {

            if (!updatedBoardStages
              || isEqual(updatedBoardStages, this.originalBoardStages)) {
              return EMPTY;
            }

            this.originalBoardStages = updatedBoardStages;

            return this.boardStagesSender.send(updatedBoardStages);
          })
        )
        .subscribe();
  }

  ngOnDestroy(): void {

    if (this.boardStagesRequestQueueSubscription) {
      this.boardStagesRequestQueueSubscription.unsubscribe();
    }
  }

  onListStageChange(
    listId: string,
    stage: string,
  ): void {

    if (!this.dataLoaded) {
      return;
    }

    if (!stage) {
      delete this.selectedListStages[listId];

    } else {
      this.selectedListStages[listId] = stage;
    }

    const boardStagesRequest: BoardStages = {
      pending: this.getSelectedListsByStage('pending'),
      inProgress: this.getSelectedListsByStage('inProgress'),
      completed: this.getSelectedListsByStage('completed'),
    };

    this.boardStagesRequestQueue.next(boardStagesRequest);
  }

  protected getLists(): any[] {

    if (this.lists) {
      return this.lists.lists;
    }

    return [];
  }

  private getSelectedListsByStage(
    stage: string,
  ): string[] {

    return Object.entries(this.selectedListStages)
      .filter(([, selectedStage]) => selectedStage === stage)
      .map(([listId]) => listId);
  }

}
